import { observable, action, computed } from 'mobx'
import { persist, create } from 'mobx-persist'

//utils
import moment from 'moment'
import _ from 'lodash'
import AdventureService from 'services/AdventureService';

//stores
import { Navigator } from 'router/index'
import DrawerStore from 'stores/DrawerStore';

//models
import Adventure from 'models/Adventure'
import Package from 'models/Package'
import ScheduleRule from 'models/ScheduleRule';
import Helpers from '../../utils/Helpers';
import { AuthStore } from '../auth/AuthStore';


export class Category {
  @observable name;
  @observable url_name;

  constructor(json = {}) {
    this.name = json.name || ""
    this.pk_category = json.pk_category || null
    this.url_name = json.url_name || ""
  }
}

export class Operation {
  constructor(json = {}) {
    this.pk_operating = json.pk_operating || null;
    this.fk_adventure = json.fk_adventure || null
    this.name = json.name || ''
    this.startDate = json.startDate || ''
    this.endDate = json.endDate || ''
    this.tag = json.tag || ''
    this.repeat = json.repeat || ''
    this.repetition = json.repetition || ''
    this.days = json.days || json.operatingDays || []
    this.ruleTimeOperations = json.ruleTimeOperations || []
    this.operatingDayExceptions = json.operatingDayExceptions || []
    this.disableDates = json.disableDates || []
    this.rules = []
  }
}

export class RequestCustomField {
  @observable title = ''
  @observable type = ''
  @observable options = ''
  @observable icon = ''
  @observable pk_request_custom_fields = undefined

  @action setTitle = value => this.title = value
  @action setType = value => this.type = value
  @action setOptions = value => this.options = value
  @action setIcon = value => this.icon = value

  constructor(json = {}){
    this.title = json.title ? json.title : ''
    this.type = json.type ? json.type : ''
    this.options = json.options ? json.options : ''
    this.icon = json.icon ? json.icon : ''
    this.pk_request_custom_fields = json.pk_request_custom_fields ? json.pk_request_custom_fields : undefined
  }
}

export class ReserveCustomField {
  @observable title = ''
  @observable type = ''
  @observable options = ''
  @observable icon = ''
  @observable pk_reserve_custom_fields = undefined

  @action setTitle = value => this.title = value
  @action setType = value => this.type = value
  @action setOptions = value => this.options = value
  @action setIcon = value => this.icon = value

  constructor(json = {}){
    this.title = json.title ? json.title : ''
    this.type = json.type ? json.type : ''
    this.options = json.options ? json.options : ''
    this.icon = json.icon ? json.icon : ''
    this.pk_reserve_custom_fields = json.pk_reserve_custom_fields ? json.pk_reserve_custom_fields : undefined
  }
}

class ClassAdventureStore {

  @observable showOnBoarding = true;
  @observable fetchingAllAdventures = true;

  @observable requestsToUpdateToException = []
  @persist('object') @observable adventure = new Adventure();
  @persist('list') @observable seenedAdventuresPublisheds = [];
  @observable getAdventureLoading = false;
  @observable loadingUpdateAdventure = false;
  @observable editingAdventure = false;
  @observable selectedDate = new Date();
  @observable categories = [];
  @observable allAdventures = []
  @observable loadingRegisterDraft = false;
  @observable loadingRegisterDraftProgress = 1;
  @observable loadingUploadingImages = false;
  @observable loadingUploadingImagesProgress = 1;
  @observable isDialogSchedule = true;

  // Locales
  @observable comesFromAdventureWithNoCep;

  @action
  setDialogSchedule(boolean) {
    this.isDialogSchedule = boolean
  }

  @action
  setBlockParticipationHours(value) {
    this.adventure.block_participation_hours_before = Number(value).toFixed(0)
  }
  @action
  setBlockRequestHours(value) {
    this.adventure.block_request_hours_before = Number(value).toFixed(0)
  }

  @action
  addCustomField(json = {}) {
    this.adventure.requestCustomFields = [...this.adventure.requestCustomFields, new RequestCustomField(json)]
  }

  @action
  removeCustomField(field) {
    const index = this.adventure.requestCustomFields.findIndex(item => item.title === field.title)
    if (index !== -1) {
      this.adventure.requestCustomFields.splice(index, 1)
    }
    if(field.pk_request_custom_fields){
      AdventureService.removeRequestCustomField({pkField: field.pk_request_custom_fields})
    }
  }

  @action
  getCustomFields() {
    AdventureService.getRequestCustomFields(this.adventure.pk_adventure).then(data => {
      if(data) {
        this.adventure.requestCustomFields = data.data.map(item => new RequestCustomField(item))
      }
    })
  }

  @action
  saveCustomFields() {
    const fields = this.adventure.requestCustomFields.filter(field => !!field.title && !!field.type)
    AdventureService.insertRequestCustomFields({fields, pkAdventure: this.adventure.pk_adventure})
  }

  @action
  addReserveCustomField(json = {}) {
    this.adventure.reserveCustomFields = [...this.adventure.reserveCustomFields, new ReserveCustomField(json)]
  }

  @action
  removeReserveCustomField(field) {
    const index = this.adventure.reserveCustomFields.findIndex(item => item.title === field.title)
    if (index !== -1) {
      this.adventure.reserveCustomFields.splice(index, 1)
    }
    if(field.pk_reserve_custom_fields){
      AdventureService.removeReserveCustomField({pkField: field.pk_reserve_custom_fields})
    }
  }

  @action
  getReserveCustomFields(pk_adventure) {
    return AdventureService.getReserveCustomFields(this.adventure.pk_adventure ? this.adventure.pk_adventure : pk_adventure).then(data => {
      if(data) {
        this.adventure.reserveCustomFields = data.data.map(item => new ReserveCustomField(item))
        return data.data
      }
    })
  }

  @action
  saveReserveCustomFields() {
    const fields = this.adventure.reserveCustomFields.filter(field => !!field.title && !!field.type)
    AdventureService.insertReserveCustomFields({fields, pkAdventure: this.adventure.pk_adventure})
  }

  @action
  setSeenAdventurePublished() {
    this.seenedAdventuresPublisheds = this.getAllPublishAdventures
  }

  @action
  clearAdventureModel() {
    this.adventure = new Adventure()
  }

  @computed
  get getAllDraftAdventures() {
    return this.allAdventures.filter(adventure => adventure.isDraft)
  }

  @computed
  get getAllPublishAdventures() {
    return this.allAdventures.filter(adventure => adventure.isPublished)
  }

  @computed
  get getAllWaitingAdventures() {
    return this.allAdventures.filter(adventure => adventure.isWaiting)
  }

  @computed
  get getAllDisabledAdventures() {
    return this.allAdventures.filter(adventure => adventure.isDisabled)
  }

  @computed 
  get readyToAdvertise() {
    return  !!this.adventure.title &&
            !!this.adventure.description &&
            !!this.adventure.category.name &&
            !!this.adventure.formatedAddress
  }

  @computed
  get readyToSellVoucher() {
    return !!this.adventure.packages.length &&
           !!this.adventure.voucher_expiration_days &&
           !!this.adventure.cancel_rules &&
           AuthStore.bankDataComplete &&
           AuthStore.personalDataComplete &&
           AuthStore.businessDataComplete 
  }

  @computed
  get readyToSellOnlineSchedule() {
    return  !!this.adventure.packages.length &&
            !!this.adventure.cancel_rules &&
            AuthStore.bankDataComplete &&
            AuthStore.personalDataComplete &&
            AuthStore.businessDataComplete &&
            !!this.adventure.scheduleRules.length &&
            !!this.adventure.duration &&
            !!this.adventure.block_participation_hours_before &&
            !!this.adventure.block_request_hours_before
  }

  @computed
  get readyToContinueOnlineSchedule() {
    return !!this.adventure.packages.length &&
           !!this.adventure.cancel_rules &&
           AuthStore.bankDataComplete &&
           AuthStore.personalDataComplete &&
           AuthStore.businessDataComplete 
  }

  @action
  setEdditingAdventure() {
    this.editingAdventure = true;
  }

  @action
  unsetEdditingAdventure() {
    this.editingAdventure = false;
  }

  setGoBackListener() {
    Navigator.history.listen((location, action) => {
      const locationParams = location.pathname.split('/');
      const stepIndex = locationParams.findIndex(routes => routes.includes('step-'));

      if (!locationParams[stepIndex]) return
      const currentStep = Number(locationParams[stepIndex].replace('step-', ''));
      DrawerStore.setCurrentStep(currentStep);
    })
  }

  @action
  goToLastStep() {
    Navigator.history.goBack()
    DrawerStore.backOneStep()
  }

  @action
  goToNextStep({ ...params }, action = 'push') {
    const { location, history } = Navigator;

    const locationParams = location.pathname.split('/');
    const stepIndex = locationParams.findIndex(routes => routes.includes('step-'));
    const currentStep = Number(locationParams[stepIndex].replace('step-', ''));

    locationParams[stepIndex] = 'step-' + (currentStep + 1)
    history[action](locationParams.join('/'), { ...params })

    DrawerStore.plusOneStep()
  }

  //adventure setters
  @action
  resetAdventure() {
    this.adventure = new Adventure();
  }

  @action
  companyAdventures() {
    this.fetchingAllAdventures = true
    return AdventureService.getAdventuresByCompany()
      .then(res => {
        this.fetchingAllAdventures = false;
        this.showOnBoarding = !res.data.length
        this.allAdventures = res.data.map(adventure => new Adventure(adventure))
      })
      .catch(err => console.log(err.data))
  }

  @action
  getAdventureById(pkAdventure) {
    this.getAdventureLoading = true;
    return AdventureService.getAdventureById(pkAdventure)
      .then(res => {
        this.adventure = new Adventure(res.data)
        this.adventure.scheduleRules = this.adventure.scheduleRules.map(rule => new ScheduleRule(rule));
        //this.adventure.operatings = this.adventure.operatings.map(operating => new Operation(operating));
        this.getAdventureLoading = false;
      })
      .catch(err => console.log(err))
  }

  @action
  getCategories() {
    AdventureService.getCategories()
      .then(res => {
        this.categories = res.data.map(category => new Category(category));
        // this.categories.forEach(category => {
        //   this.getTypesByCategoriesId(category.pk_category_type);
        // })
      })
      .catch(err => console.log(err))
  }

  @action
  getTypesByCategoriesId(category_id) {
    AdventureService.getTypesByCategoriesId(category_id)
      .then(res => {
        const categoryIndex = this.categories
          .map(category => category.pk_category_type)
          .indexOf(category_id)

        this.categories[categoryIndex].types = res.data;
      })
      .catch(err => console.log(err))
  }

  @action
  setLoadingRegisterDraftProgress(progress) {
    this.loadingRegisterDraftProgress = progress
    this.loadingUploadingImagesProgress = progress
  }

  @action
  uploadImageToAws(image){
    this.loadingUploadingImages = true;
    this.loadingUploadingImagesProgress = 1;

    const data = new FormData();
    data.append('image', image)

    return AdventureService.uploadImageToAws(data, this.setLoadingRegisterDraftProgress.bind(this))
      .then(res => {
        this.loadingUploadingImages = false;
        return res
      })
      .catch(err => {
        this.loadingUploadingImages = false;
        throw err;
      })
  }

  @action
  saveDraft(upload) {
    this.loadingRegisterDraft = true;
    if (upload) {
      this.loadingUploadingImages = true;
      this.loadingUploadingImagesProgress = 1;
    }

    return AdventureService.saveDraft(this.adventure, this.setLoadingRegisterDraftProgress.bind(this))
      .then((res) => {
        this.loadingRegisterDraft = false;
        this.loadingUploadingImages = false;
        DrawerStore.setLoading();
        return this.adventure.pk_adventure = res.data.pk_adventure;
      })
      .catch((err) => {
        this.loadingUploadingImages = false;
        DrawerStore.unsetLoading();
      });
  }

  @action
  setOnlyPublic(value) {
    this.adventure.only_public = value === 'public' ? true : false
  }

  @action
  setWhatToKnowItems(items) {
    this.adventure.whatToKnow = items
  }

  @action
  setAdventureMin(min) {
    this.adventure.min = min
  }

  @action
  setAdventureMax(max) {
    this.adventure.max = max
  }

  @action
  setAdventureTitle(title) {
    this.adventure.title = title
    this.adventure.url_name = Helpers.formatUrl(title);
  }

  @action
  setAdventureDuration(duration) {
    this.adventure.duration = duration
  }

  @action
  setAdventureLongDescription(long_description) {
    this.adventure.long_description = long_description
  }

  @action
  setAdventureDescription(description) {
    this.adventure.description = description
  }

  @action
  setAdventureType(fk_type, fk_category_type) {
    this.adventure.fk_type = fk_type
    if (fk_category_type) this.adventure.fk_category_type = fk_category_type
  }

  @action
  setAdventureCategory(category) {
    this.adventure.category = category
    this.adventure.fk_category = category.pk_category
  }

  @action
  setBanner(banner) {
    this.adventure.banner = banner;
  }

  @action
  setThumbnail(image) {
    this.adventure.image = image;
  }

  @action
  addImage(image) {
    this.adventure.images.push(image);
  }

  @action
  removeImage(index) {
    this.adventure.images.splice(index, 1);
  }

  // Locale
  @action
  setLocale(locale) {
    this.adventure.cep = locale.cep;
    this.adventure.city = locale.city;
    this.adventure.lat = locale.lat;
    this.adventure.lng = locale.lng;
    this.adventure.number = locale.number;
    this.adventure.state = locale.state;
    this.adventure.street = locale.street;
    this.adventure.neighborhood = locale.neighborhood;
    
    this.setFormatedAddress(`${locale.street}, ${locale.number} - ${locale.neighborhood}, ${locale.city} - ${locale.state}, ${locale.cep}`)
  }

  @action
  setFormatedAddress(formatedAddress) {
    this.adventure.formatedAddress = formatedAddress;
  }

  @action
  removeFormatedAddress() {
    this.adventure.formatedAddress = '';
    this.setLocaleBackCondition();
  }

  @action
  setLocaleBackCondition() {
    this.comesFromAdventureWithNoCep = true;
  }

  @action
  removeLocaleBackCondition() {
    this.comesFromAdventureWithNoCep = false;
  }

  @action
  removeLatLng() {
    this.adventure.lat = '';
    this.adventure.lng = '';
  }

  @action
  setLatLng(lat, lng) {
    this.adventure.lat = lat;
    this.adventure.lng = lng;
  }

  // Meeting Point
  @action
  setMeetingPoint(state) {
    this.adventure.meetingPointCep = state.meetingPointCep;
    this.adventure.meetingPointCity = state.meetingPointCity;
    this.adventure.meetingPointLat = state.meetingPointLat;
    this.adventure.meetingPointLng = state.meetingPointLng;
    this.adventure.meetingPointNumber = state.meetingPointNumber;
    this.adventure.meetingPointState = state.meetingPointState;
    this.adventure.meetingPointStreet = state.meetingPointStreet;
    this.adventure.meetingPointNeighborhood = state.meetingPointNeighborhood;
  }

  @action
  setMeetingPointFormatedAddress(meetingPointFormatedAddress) {
    this.adventure.meetingPointFormatedAddress = meetingPointFormatedAddress;
  }

  @action
  removeMeetingPointFormatedAddress() {
    this.adventure.meetingPointFormatedAddress = '';
    this.setMeetingPointBackCondition();
  }

  @action
  setMeetingPointBackCondition() {
    this.comesFromMeetingPointAdventureWithNoCep = true;
  }

  @action
  removeMeetingPointBackCondition() {
    this.comesFromMeetingPointAdventureWithNoCep = false;
  }

  @action
  removeMeetingPointLatLng() {
    this.adventure.meetingPointLat = '';
    this.adventure.meetingPointLng = '';
  }

  @action
  setMeetingPointLatLng(lat, lng) {
    this.adventure.meetingPointLat = lat;
    this.adventure.meetingPointLng = lng;
  }

  @action
  changeSameLocaleAndMeetingPoint() {
    this.adventure.sameLocaleAndMeetingPoint = ~~(!this.adventure.sameLocaleAndMeetingPoint);
  }

  // Additional Informations
  @action
  setAboutTheOperator(description) {
    this.adventure.aboutTheOperator = description;
  }

  @action
  setWhyLiveThisExperience(description) {
    this.adventure.whyLiveThisExperience = description;
  }

  @action
  setImportant(items) {
    if (!!items[0].value) {
      this.adventure.important = items;
    } else {
      this.adventure.important = [];
    }
  }

  @action
  setWhatDoesNotInclude(items) {
    if (!!items[0].value) {
      this.adventure.whatDoesNotInclude = items;
    } else {
      this.adventure.whatDoesNotInclude = [];
    }
  }

  @action
  setRequirements(items) {
    if (!!items[0].value) {
      this.adventure.requirements = items;
    } else {
      this.adventure.requirements = [];
    }
  }

  @action
  setWhatToTake(items) {
    if (!!items[0].value) {
      this.adventure.whatToTake = items;
    } else {
      this.adventure.whatToTake = [];
    }
  }

  @action
  setWhatsNearby(description) {
    this.adventure.whatsNearby = description;
  }

  @action
  setWhatToWear(items) {
    if (!!items[0].value) {
      this.adventure.whatToWear = items;
    } else {
      this.adventure.whatToWear = [];
    }
  }

  @action
  setOtherRules(description) {
    this.adventure.otherRules = description;
  }

  @action
  setScript(items) {
    if (!!items[0].value) {
      this.adventure.script = items;
    } else {
      this.adventure.script = [];
    }
  }

  @action
  createPackage(selected_package) {
    this.adventure.packages.push(new Package(selected_package))
    this.checkPackageFeatured(selected_package, this.adventure.packages.length - 1)
  }

  @action
  editPackage(selected_package, index) {
    this.adventure.packages.splice(index, 1, new Package(selected_package))
    this.checkPackageFeatured(selected_package, index)
  }

  @action
  checkPackageFeatured(selected_package, index) {
    if (selected_package.isDefault) {
      this.adventure.packages.forEach(adventure_package => adventure_package.isDefault = false)
      this.adventure.packages.splice(index, 1)
      this.adventure.packages.unshift(new Package(selected_package))
    }
  }

  // @action
  // createSchedule(data) {
  //   //const operation = new Operation(selected_operation)
  //   //when creating a new operation to the following days
  //   //this action set an end date to the edited operation
  //   //so we have to filter the exceptions that it is only in 
  //   //the period of the operation
  //   // operation.operatingDayExceptions = operation.operatingDayExceptions
  //   //   .filter(exception => {
  //   //     return moment(exception.date).isAfter(moment(operation.startDate))
  //   //   })
  //   // this.adventure.operatings.push(operation)
    
  //   return AdventureService.createSchedule({ data }).then(res => {
  //             let rule = new ScheduleRule(res.data);
  //             this.adventure.scheduleRules.push(rule)
  //           })
  // }

  // @action
  // editSchedule(operation, index) {
  //   this.adventure.operatings.splice(index, 1, new Operation(operation))
  // }

  // @action
  // editScheduleException(exception, index, exceptionIndex) {
  //   const operation = { ...this.adventure.operatings[index] }
  //   operation.operatingDayExceptions.splice(exceptionIndex, 1, exception)
  //   this.adventure.operatings.splice(index, 1, new Operation(operation))
  // }

  // @action
  // createScheduleException(operation_index, exception) {

  //   // send requests to a array on store so when the adventure is update
  //   // this requests will be update to use a pk_rule_time_operation of 
  //   // the exception created
  //   // this.requestsToUpdateToException = [
  //   //   ...this.requestsToUpdateToException,
  //   //   this.adventure.operatings[operation_index].requests.filter(request => Helpers.isSameDate(request.date, exception.date))
  //   // ]

  //   this.adventure.operatings[operation_index].operatingDayExceptions.push(exception)
  // }

  @action
  setSelectedDate(selected_date) {
    this.selectedDate = selected_date
  }

  // @action
  // deleteAllOperation(operation_index) {
  //   this.adventure.operatings.splice(operation_index, 1)
  // }

  // @action
  // deleteScheduleRule(data) {
  //   return AdventureService.deleteScheduleRule(data)
  //     .then(res => {
  //       console.log("Excluido com sucesso");
  //     })
  //     .catch(err => {throw err})
  // }

  // @action
  // deleteTheException(operation_index, exception_index) {

  //   // if there is requests to be update and the user decided to delete
  //   // the exception, then filter this requests out
  //   // this.requestsToUpdateToException = this.requestsToUpdateToException.filter(request => !Helpers.isSameDate(request.date, exception.date))

  //   this.adventure.operatings[operation_index].operatingDayExceptions.splice(exception_index, 1)
  // }

  // @action
  // deleteOnlyThisDay(operation_index) {
  //   this.adventure.operatings[operation_index].disableDates.push({ date: this.selectedDate })
  // }

  // @action
  // setEndDateToOperation(endDate, index) {
  //   const operation = { ...this.adventure.operatings[index] }
  //   operation.endDate = endDate
  //   //when creating a new operation to the following days
  //   //this action set an end date to the edited operation
  //   //so we have to filter the exceptions that it is only in 
  //   //the period of the operation
  //   // operation.operatingDayExceptions = operation.operatingDayExceptions
  //   //   .filter(exception => {
  //   //     return moment(exception.date).isAfter(moment(operation.startDate)) && moment(exception.date).isBefore(moment(endDate))
  //   //   })

  //   //replace the operation
  //   this.adventure.operatings[index] = operation
  // }

  @action
  changeStatusToPublish(pk_adventure) {
    return AdventureService.changeStatusToEnabled(pk_adventure)
      .then(res => {
        const index = _.findIndex(this.allAdventures, adventure => adventure.pk_adventure === res.data.pk_adventure)
        this.allAdventures.splice(index, 1, new Adventure(res.data))
      })
      .catch(err => console.log(err))
  }

  @action
  changeStatusToDisabled(pk_adventure) {
    return AdventureService.changeStatusToDisabled(pk_adventure)
      .then(res => {
        const index = _.findIndex(this.allAdventures, adventure => adventure.pk_adventure === res.data.pk_adventure)
        this.allAdventures.splice(index, 1, new Adventure(res.data))
      })
      .catch(err => console.log(err))
  }

  @action
  changeStatusToEnabled(pk_adventure) {
    return AdventureService.changeStatusToEnabled(pk_adventure)
      .then(res => {
        const index = _.findIndex(this.allAdventures, adventure => adventure.pk_adventure === res.data.pk_adventure)
        this.allAdventures.splice(index, 1, new Adventure(res.data))
      })
      .catch(err => console.log(err))
  }

  @action
  deleteAdventure(pk_adventure) {
    return AdventureService.deleteAdventure(pk_adventure)
      .then(res => {
        if (res) {
          const index = _.findIndex(this.allAdventures, adventure => adventure.pk_adventure === pk_adventure)
          this.allAdventures.splice(index, 1)
        }
      })
  }

  @action
  update(upload) {
    this.loadingUpdateAdventure = true

    if (upload) {
      this.loadingUploadingImages = true;
      this.loadingUploadingImagesProgress = 1;
    }

    //this.saveCalendar()

    return AdventureService.update({ pkAdventure: this.adventure.pk_adventure, ...this.adventure }, this.setLoadingRegisterDraftProgress.bind(this))
      .then(res => {
        this.loadingUploadingImages = false;
        this.loadingUpdateAdventure = false
        //this.adventure = new Adventure(res.data);
      })
  }

  async saveCalendar() {

    //params to create or edit the calendar on calendar api
    const paramsCalendar = {
      summary: this.adventure.title,
      pkAdventure: this.adventure.pk_adventure,
      google_calendar_id: this.adventure.google_calendar_id
    }

    //create or edit the calendar on calendar api
    const savedCalendar = await AdventureService.saveCalendar(paramsCalendar)

    //save google id to model stored
    this.adventure.google_calendar_id = savedCalendar.google_calendar_id

    //params to save the calendar id to adventure in db
    const paramsCalendarId = {
      pkAdventure: this.adventure.pk_adventure,
      google_calendar_id: this.adventure.google_calendar_id
    }

    //set the calendar id on the adventure in db
    await AdventureService.setAdventureCalendarId(paramsCalendarId)
  }

  @action
  updateSellingInformation() {
    this.adventure.credit_card_enabled = this.adventure.paymentMethods.credit_card
    this.adventure.boleto_enabled = this.adventure.paymentMethods.boleto
    this.adventure.local_payment_enabled = this.adventure.paymentMethods.local

    return this.update()
  }

  @action
  enableVoucher() {
    this.adventure.voucher_enabled = true;
    this.adventure.online_scheduling = false;
  }

  @action
  enableSchedule() {
    this.adventure.voucher_enabled = false;
    this.adventure.online_scheduling = true;

    this.saveCalendar()
  }

  @action
  pushNewScheduleRule(rule) {
    if (this.adventure) {
      const rules = [...this.adventure.scheduleRules, rule]
      this.adventure.scheduleRules = rules
    }
  }

  @action
  updatePackage() {
    this.loadingUpdateAdventure = true
    return AdventureService.updatePackage({
      adventure: this.adventure
    })
      .then(res => {
        this.loadingUpdateAdventure = false
        //this.adventure = new Adventure(res.data);
      })
      .catch(err => { throw err })
  }

  @action
  updateOperatings() {
    this.loadingUpdateAdventure = true
    return AdventureService.updateOperatings({ adventure: this.adventure })
      .then(res => {
        this.loadingUpdateAdventure = false
        this.adventure = new Adventure(res.data);
      })
      .catch(err => { throw err })
  }

  @action
  removeAboutTheOperator() {
    this.adventure.aboutTheOperator = ''
  }

  @action
  removeWhyLiveThisExperience() {
    this.adventure.whyLiveThisExperience = ''
  }

  @action
  removeOtherRules() {
    this.adventure.otherRules = ''
  }

  @action
  removeWhatsNearby() {
    this.adventure.whatsNearby = ''
  }

  @action
  removeWhatToKnow() {
    this.adventure.whatToKnow = []
  }

  @action
  removeRequirements() {
    this.adventure.requirements = []
  }

  @action
  removeImportant() {
    this.adventure.important = []
  }

  @action
  removeWhatDoesNotInclude() {
    this.adventure.whatDoesNotInclude = []
  }

  @action
  removeWhatToTake() {
    this.adventure.whatToTake = []
  }

  @action
  removeWhatToWear() {
    this.adventure.whatToWear = []
  }

  @action
  removeScript() {
    this.adventure.script = []
  }

  @action
  removePackage(index) {
    this.adventure.packages.splice(index, 1);
  }

  @action
  addQuestionToFAQ(question) {
    this.adventure.frequentlyQuestion.push(question)
  }

  @action
  removeQuestionToFAQ(index) {
    this.adventure.frequentlyQuestion.splice(index, 1)
  }

  @action
  editQuestionToFAQ(question, index) {
    this.adventure.frequentlyQuestion.splice(index, 1, question)
  }

  @action
  setTerms(terms) {
    this.adventure.terms = terms
  }

  @action
  setVoucherExpiration(expiration) {
    this.adventure.voucher_expiration_days = expiration
  }

  @action
  setCancelRules(cancel) {
    this.adventure.cancel_rules = cancel
  }

  @computed
  get paymentMethods() {
    return this.adventure.paymentMethods
  }

  @action
  toggleFillAllReserves() {
    this.adventure.fill_all_reserves = !this.adventure.fill_all_reserves
  }

  @action
  togglePaymentMethod(method) {
    this.adventure.paymentMethods[method] = !this.paymentMethods[method]
  }

  @action
  toggleCheckBeforeRequest() {
    this.adventure.check_before_request = !this.adventure.check_before_request
  }
}

const hydrate = create({})

export const AdventureStore = new ClassAdventureStore();
export const rehydrateAdventureStore = hydrate('AdventureStore', AdventureStore).rehydrate



// WEBPACK FOOTER //
// ./src/modules/adventure/AdventureStore.js